import React from "react";

import { admin_cse } from "@/config/roles";
import { ADVANCED_CARE_PROGRAM } from "@/core/constants";
import { cn } from "@/lib/helpers/cn";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { getACPConsentRows } from "@/features/acp/helpers/consent-versions";
import { isACPEligibleCarrier } from "@/lib/helpers/isACPEligibleCarrier";
import { isAuthorized } from "@/utils/authToken";
import type { PatientConsent, UUID } from "@/core/types";
import { usePolicyMetadata } from "@/features/consent/api/queries/usePolicyMetadata";

import { DetailItem } from "./SectionDetail";
import { MemberProfileAcpBadge } from "./MemberProfileAcpBadge";
import { MemberProfileACPSectionSkeleton } from "./MemberProfileACPSectionSkeleton";
import { OptOutButton } from "./OptOutButton";
import { SectionDetailList } from "./SectionDetailList";
import { SectionDetailListHeader } from "./SectionDetailListHeader";

type MemberProfileACPSectionProps = {
  consents: PatientConsent[];
  insuranceCarrier: string;
  patientId: UUID;
};

export function MemberProfileACPSection({
  consents,
  insuranceCarrier,
  patientId,
}: MemberProfileACPSectionProps) {
  const isEligible = isACPEligibleCarrier(insuranceCarrier);
  const isCSE = isAuthorized(admin_cse);

  const { data: policyMetadata, isLoading: isLoadingPolicyMetadata } = usePolicyMetadata();

  if (isEligible && (isLoadingPolicyMetadata || !policyMetadata)) {
    return <MemberProfileACPSectionSkeleton />;
  }

  if (!isEligible) {
    return (
      <DashboardCard2 title="Advanced Care Program Status">
        <dl className="tw:m-0!">
          <DetailItem
            detail={{
              key: "consent_status",
              label: "Consent Status",
              value: <MemberProfileAcpBadge status="NOT_ELIGIBLE" />,
            }}
          />
        </dl>
      </DashboardCard2>
    );
  }

  const currentVersion = policyMetadata[ADVANCED_CARE_PROGRAM].current_version;
  const rows = getACPConsentRows({ consents, currentVersion, insuranceCarrier });
  const currentRow = rows.find((row) => row.version === currentVersion);

  const shouldShowOptOutButton = isCSE && currentRow && currentRow.consentStatus !== "OPTED_OUT";

  return (
    <DashboardCard2
      Actions={
        shouldShowOptOutButton && (
          <OptOutButton
            patientId={patientId}
            patientName={currentRow.consent?.responseGivenByName ?? ""}
            sortKey={currentRow.consent?.sortKey ?? ""}
          />
        )
      }
      title="Advanced Care Program Status"
    >
      <div className="tw:flex tw:flex-col tw:gap-5">
        <SectionDetailListHeader />

        <ul className="tw:m-0! tw:list-none tw:p-0!">
          {rows.map((row, index) => (
            <li
              className={cn(
                "tw:py-4 tw:first:pt-0 tw:last:pb-0",
                index < rows.length - 1 && "tw:border-b tw:border-stroke-quaternary",
              )}
              key={row.version}
            >
              <SectionDetailList patientId={patientId} row={row} />
            </li>
          ))}
        </ul>
      </div>
    </DashboardCard2>
  );
}
